import {
    Autocomplete,
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    TextField,
    DialogTitle,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
    Stack,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useTranslation } from '../common/i18n/useTranslation';
import { useEffect, useState } from 'react';
import { getPropertyName } from '../common/helper/getPropertyName';
import { envDevUserClaims, useJwtCreationContext } from './contexts/JwtCreationContext';
import { DevUserClaims } from '../utils/interfaces';
import { endpointConfig } from '../configuration/configuration';
import { makeStyles } from 'tss-react/mui';
import { theme } from '../AppTheme';
import {
    AdminCompanyRegistrationApiService,
    AdminUsersApiService,
    CompanyRegistrationDto,
    UserClaimsDto,
    UserInfoDto,
} from '../api';
import CustomWbTextField from '../common/components/input/CustomWbTextField';
import { usePersistedState } from '@plusng/react-common-components';
import { useClientCompanyRegistrationContext } from './client/companyRegistration/context/ClientCompanyRegistrationContext';
import { setLocallyStoredUser, transformInformation } from '../utils/utils';

const useStyles = makeStyles()((theme) => ({
    selectItems: {
        maxHeight: '33%',
    },
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    compRegState: {
        marginBottom: theme.spacing(2),
    },
    rightsSelector: {
        marginBottom: theme.spacing(2),
    },
}));

interface ChangeDevUserDialogProps {
    open: boolean;
    handleCloseChangeDevUserDialog: () => void;
}

function ChangeDevUserDialog(props: Readonly<ChangeDevUserDialogProps>) {
    const { classes } = useStyles();
    const translate = useTranslation();
    const [users, setUsers] = useState<UserClaimsDto[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserClaimsDto>();
    const [companyOfSelectedUser, setCompanyOfSelectedUser] = useState<CompanyRegistrationDto>();
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
    const [thirdPartyContracts, setThirdPartyContracts] = useState<boolean>(false);

    const [companyRegistrationState, setCompanyRegistrationState] =
        useState<CompanyRegistrationDto.registrationState>();
    const [devClaims, setDevClaims] = useState<DevUserClaims>(envDevUserClaims);

    const { setClaims } = useJwtCreationContext();
    const { setRegistration, setUserInfo, userInfo, isAdmin } = useClientCompanyRegistrationContext();

    const entitlementGroups = [
        `${endpointConfig.env.ROLE_PREFIX}.BEAM_USER`,
        `${endpointConfig.env.ROLE_PREFIX}.BEAM_ADMIN`,
    ];
    const [entitlementGroup, setEntitlementGroup] = usePersistedState<string[]>('EntitlementGroup', 'value', [
        `${endpointConfig.env.ROLE_PREFIX}.BEAM_USER`,
        `${endpointConfig.env.ROLE_PREFIX}.BEAM_ADMIN`,
    ]);

    useEffect(() => {
        if (userInfo && Array.isArray(users)) {
            const userId = users.find((user) => user.username === userInfo.userId)?.id;
            if (userId) {
                void handleUserChange(userId);
            }
        }
    }, [users]);

    const saveUserClaims = async () => {
        await updateData();
        props.handleCloseChangeDevUserDialog();
    };

    const updateTermsOfUse = async () => {
        const postBody = transformInformation({
            ...companyOfSelectedUser,
            termsOfUseApproved: termsAccepted,
            registrationState: companyRegistrationState,
        }) as CompanyRegistrationDto;
        const updatedCompany = await AdminCompanyRegistrationApiService.updateCompanyRegistration1(
            selectedUser?.companyId ?? '',
            { ...postBody, hasThirdPartyContracts: thirdPartyContracts, usageMode: companyOfSelectedUser?.usageMode }
        );
        return updatedCompany;
    };

    const updateData = async () => {
        setEntitlementGroup(devClaims.entitlement_group);

        if (selectedUser?.companyId) {
            const updatedCompany = await updateTermsOfUse();
            setRegistration(updatedCompany);
        } else {
            setRegistration(undefined);
        }

        setClaims(devClaims);

        let updatedUserInfo: UserInfoDto | undefined = undefined;
        if (selectedUser) {
            setLocallyStoredUser({
                id: selectedUser?.id ?? '',
                username: selectedUser?.username ?? '',
                familyName: selectedUser?.familyName ?? '',
                givenName: selectedUser?.givenName ?? '',
                email: selectedUser?.email ?? '',
            });

            updatedUserInfo = {
                ...userInfo,
                email: selectedUser?.email,
                entitlementGroup: [...devClaims.entitlement_group],
                familyName: selectedUser?.familyName,
                givenName: selectedUser?.givenName,
                userId: selectedUser?.username,
                phoneNumber: '',
                salutation: UserInfoDto.salutation.OTHER,
            };
        } else {
            updatedUserInfo = {
                ...userInfo,
                email: devClaims.email,
                entitlementGroup: [...devClaims.entitlement_group],
                familyName: devClaims.family_name,
                givenName: devClaims.given_name,
                phoneNumber: '',
                salutation: UserInfoDto.salutation.OTHER,
            };
        }
        setUserInfo(updatedUserInfo);
    };

    const updateClaims = (property: string, value: string | string[]) => {
        const update: DevUserClaims = {
            ...devClaims,
            [property]: value,
        };
        setDevClaims(update);
    };

    const updateEntitlementGroups = () => {
        updateClaims(
            getPropertyName(envDevUserClaims, (x) => x.entitlement_group),
            entitlementGroup
        );
    };

    const getUsers = async () => {
        const userClaims = await AdminUsersApiService.getUsersClaims();
        setUsers(userClaims);
    };

    const getCompany = async (companyId: string) => {
        const company = await AdminCompanyRegistrationApiService.getCompanyRegistration(companyId);
        setCompanyOfSelectedUser(company);
        if (company.termsOfUseApproved !== undefined) {
            setTermsAccepted(company.termsOfUseApproved);
        }
        if (company.registrationState !== undefined) {
            setCompanyRegistrationState(company.registrationState);
        }
        if (company.hasThirdPartyContracts !== undefined) {
            setThirdPartyContracts(company.hasThirdPartyContracts);
        }
    };

    useEffect(() => {
        getUsers();
        updateEntitlementGroups();
    }, []);

    const getEntitlementGroups = (
        selectedEntitlementGroups: string[], // all selected items
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string>
    ) => {
        let filteredEntitlementGroups: string[] = [];
        if (reason === 'selectOption') {
            filteredEntitlementGroups = filteredEntitlementGroups.concat(selectedEntitlementGroups);
        } else if (reason === 'removeOption') {
            filteredEntitlementGroups = devClaims.entitlement_group;
            if (selectedEntitlementGroups.length !== 0) {
                for (const elem of selectedEntitlementGroups) {
                    filteredEntitlementGroups = filteredEntitlementGroups?.filter((e) => e === elem);
                }
            } else {
                filteredEntitlementGroups = filteredEntitlementGroups?.filter((e) => e !== details?.option);
            }
        }

        return filteredEntitlementGroups;
    };

    const handleEntitlementGroupChange = (
        selectedEntitlementGroups: string[], // all selected items
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string>
    ) => {
        updateClaims(
            getPropertyName(envDevUserClaims, (x) => x.entitlement_group),
            getEntitlementGroups(selectedEntitlementGroups, reason, details)
        );
    };

    const handleUserChange = async (id: string) => {
        const _selectedUser = users.find((user) => user.id === id);
        if (_selectedUser) {
            setSelectedUser(_selectedUser);

            const update: DevUserClaims = {
                ...devClaims,
                [getPropertyName(envDevUserClaims, (x) => x.aud)]: _selectedUser.id,
                [getPropertyName(envDevUserClaims, (x) => x.client_id)]: _selectedUser.id,
                [getPropertyName(envDevUserClaims, (x) => x.email)]: _selectedUser.email,
                [getPropertyName(envDevUserClaims, (x) => x.family_name)]: _selectedUser.familyName,
                [getPropertyName(envDevUserClaims, (x) => x.given_name)]: _selectedUser.givenName,
                [getPropertyName(envDevUserClaims, (x) => x.sub)]: _selectedUser.username,
            };
            setDevClaims(update);

            if (_selectedUser.companyId) {
                await getCompany(_selectedUser.companyId);
            }
        }
    };

    const handleUserSelectChange = async (event: SelectChangeEvent<string>) => {
        if (event.target.value) {
            await handleUserChange(event.target.value);
        }
    };

    const termsAcceptedChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTermsAccepted(event.target.checked);
    };

    const handleCompanyRegistrationState = (event: SelectChangeEvent<string>) => {
        if (event.target.value) {
            const state = event.target.value as keyof typeof CompanyRegistrationDto.registrationState;
            setCompanyRegistrationState(CompanyRegistrationDto.registrationState[state]);
        }
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.handleCloseChangeDevUserDialog}
            sx={{ zIndex: theme.zIndex.drawer + 3 }}
            scroll="paper"
        >
            <DialogTitle>Edit dev user</DialogTitle>
            <DialogContent dividers>
                {isAdmin ? (
                    <>
                        <Grid container size={12} justifyContent="center" alignItems="center">
                            <FormControl variant="filled" fullWidth>
                                <InputLabel id="status-filter">{translate('user_with_company')}</InputLabel>
                                {users.length > 0 && selectedUser?.id && (
                                    <Select
                                        value={selectedUser?.id}
                                        onChange={handleUserSelectChange}
                                        MenuProps={{ classes: { paper: classes.selectItems } }}
                                    >
                                        {users?.map((user) => {
                                            return (
                                                <MenuItem key={user?.id} value={user?.id}>
                                                    <ListItemText primary={`${user.username}/${user.companyName}`} />
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid container direction={'row'} spacing={2}>
                            <Grid>
                                <CustomWbTextField
                                    label={'Email'}
                                    readonly
                                    value={selectedUser?.email ?? ''}
                                    showValueAsText
                                    type="email"
                                />
                            </Grid>
                            <Grid>
                                <CustomWbTextField
                                    label={'Family name'}
                                    readonly
                                    value={selectedUser?.familyName ?? ''}
                                    showValueAsText
                                />
                            </Grid>
                        </Grid>
                        <Grid container direction={'row'} spacing={2}>
                            <Grid>
                                <CustomWbTextField
                                    label={'Given name'}
                                    readonly
                                    value={selectedUser?.givenName ?? ''}
                                    showValueAsText
                                />
                            </Grid>
                            <Grid>
                                <CustomWbTextField
                                    label={'Sub'}
                                    readonly
                                    value={selectedUser?.username ?? ''}
                                    showValueAsText
                                />
                            </Grid>
                        </Grid>
                        {termsAccepted && (
                            <Grid>
                                <FormControl>
                                    <Stack display={'inline'}>
                                        <FormControlLabel
                                            disabled={companyOfSelectedUser === undefined}
                                            control={
                                                <Checkbox
                                                    onChange={termsAcceptedChangeHandler}
                                                    name="accept"
                                                    checked={termsAccepted}
                                                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                                                />
                                            }
                                            label={'Terms of use accepted'}
                                        />
                                    </Stack>
                                </FormControl>
                            </Grid>
                        )}
                        {companyRegistrationState && (
                            <Grid size={12} className={classes.compRegState}>
                                <FormControl variant="filled" fullWidth>
                                    <InputLabel id="test-step-type">{'Company registration state'}</InputLabel>
                                    <Select
                                        value={companyRegistrationState?.toString()}
                                        onChange={handleCompanyRegistrationState}
                                        MenuProps={{ classes: { paper: classes.selectItems } }}
                                    >
                                        {Object.values(CompanyRegistrationDto.registrationState).map((state) => {
                                            return (
                                                <MenuItem key={state} value={state}>
                                                    <ListItemText primary={state} />
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </>
                ) : (
                    <Typography variant="body1" className={classes.errorText}>
                        Only users with admin rights can change logged in user!
                    </Typography>
                )}
                <Grid>
                    <Autocomplete
                        className={classes.rightsSelector}
                        multiple
                        fullWidth
                        id="tags-standard"
                        options={entitlementGroups ?? []}
                        value={devClaims.entitlement_group}
                        getOptionLabel={(option: string) => option}
                        onChange={(_, addedCategories, reason, details) => {
                            handleEntitlementGroupChange(addedCategories, reason, details);
                        }}
                        renderValue={(selected: string[], getItemProps) => (
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {selected.map((option, index) => (
                                    // eslint-disable-next-line react/jsx-key
                                    <Chip label={option} variant="outlined" {...getItemProps({ index })} />
                                ))}
                            </Stack>
                        )}
                        slotProps={{
                            chip: { size: 'small' },
                        }}
                        renderInput={(params) => (
                            <TextField {...params} size="small" fullWidth label={'Entitlement Group'} />
                        )}
                    />
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.handleCloseChangeDevUserDialog}>{translate('cancel')}</Button>
                <Button variant="contained" onClick={saveUserClaims}>
                    {translate('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChangeDevUserDialog;
